import { createEnv } from '@t3-oss/env-core';
import { boolean } from 'boolean';
import { z } from 'zod/v4';

import {
  envDefaultBundledDataStaleTimeSeconds,
  envDefaultCacheTtlSeconds,
  envDefaultCacheUrl,
  envDefaultCronSchedule,
  envDefaultTrendWindowHours,
} from '@/config';

export const env = createEnv({
  clientPrefix: 'VITE_',
  emptyStringAsUndefined: true,
  server: {
    DATABASE_URL: z.url(),
    DATABASE_AUTH_TOKEN: z.preprocess(
      (val) => (process.env.DATABASE_URL === 'http://127.0.0.1:8080' ? undefined : val),
      z.string().min(200).optional()
    ),
    CRON_SCHEDULE: z
      .string()
      .refine(
        (val) => /^rate\([^)]+\)$|^cron\([^)]+\)$/.test(val),
        'CRON_SCHEDULE must be in the format rate(expression) or cron(expression)'
      )
      .default(envDefaultCronSchedule),
    CRON_ENABLED: z.preprocess((val) => boolean(val), z.boolean()).default(true),
    DEBUG_OUTPUT: z.preprocess((val) => boolean(val), z.boolean()).default(false),
    OPENAI_API_KEY: z.string(),
    OPENAI_ADMIN_KEY: z.string(),
    OPENAI_PROJECT_ID: z.string(),
    GOOGLE_GENERATIVE_AI_API_KEY: z.string(),
    ENABLE_AI_SUMMARIES: z.enum(['none', 'initial', 'webSearch']).default('none'),
    AWS_CF_DISTRIBUTION_ID: z.string().optional(),
    SITE_URL: z.string(),
    CACHE_TTL_SECONDS: z.coerce.number().default(envDefaultCacheTtlSeconds),
    CACHE_URL: z.string().default(envDefaultCacheUrl),
    BETTER_AUTH_SECRET: z.string(),
    BETTER_AUTH_URL: z.string(),
    GITHUB_CLIENT_ID: z.string(),
    GITHUB_CLIENT_SECRET: z.string(),
    GOOGLE_CLIENT_ID: z.string(),
    GOOGLE_CLIENT_SECRET: z.string(),
    BUNDLED_DATA_STALE_TIME_SECONDS: z.coerce
      .number()
      .default(envDefaultBundledDataStaleTimeSeconds),
    MAX_STALE_WHILE_REVALIDATE_SECONDS: z.coerce.number().default(envDefaultCacheTtlSeconds * 2),
    SENTRY_AUTH_TOKEN: z.string().optional(),
    SENTRY_DSN: z
      .string()
      .optional()
      .nullable()
      .refine((val) => !process.env.STAGE || !!val, 'Production environment requires Sentry DSN'),
    DEFAULT_TREND_WINDOW_HOURS: z.coerce.number().default(envDefaultTrendWindowHours),
  },
  client: {
    VITE_DEBUG_OUTPUT: z.preprocess((val) => boolean(val), z.boolean()).default(false),
    VITE_DEFAULT_TREND_WINDOW_HOURS: z.coerce.number().default(envDefaultTrendWindowHours),
    VITE_GOOGLE_ANALYTICS_ID: z.string().optional(),
    VITE_SITE_URL: z.string(),
    VITE_CACHE_TTL_SECONDS: z.coerce.number().default(envDefaultCacheTtlSeconds),
    VITE_SENTRY_DSN: z.url().optional().nullable(),
  },
  runtimeEnv: {
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_AUTH_TOKEN: process.env.DATABASE_AUTH_TOKEN,
    CRON_SCHEDULE: process.env.CRON_SCHEDULE,
    CRON_ENABLED: process.env.CRON_ENABLED,
    DEBUG_OUTPUT: process.env.DEBUG_OUTPUT,
    VITE_DEBUG_OUTPUT: process.env.DEBUG_OUTPUT,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    OPENAI_ADMIN_KEY: process.env.OPENAI_ADMIN_KEY,
    OPENAI_PROJECT_ID: process.env.OPENAI_PROJECT_ID,
    GOOGLE_GENERATIVE_AI_API_KEY: process.env.GOOGLE_GENERATIVE_AI_API_KEY,
    ENABLE_AI_SUMMARIES: process.env.ENABLE_AI_SUMMARIES,
    DEFAULT_TREND_WINDOW_HOURS: process.env.DEFAULT_TREND_WINDOW_HOURS,
    VITE_DEFAULT_TREND_WINDOW_HOURS: process.env.DEFAULT_TREND_WINDOW_HOURS,
    VITE_GOOGLE_ANALYTICS_ID: process.env.VITE_GOOGLE_ANALYTICS_ID,
    AWS_CF_DISTRIBUTION_ID: process.env.AWS_CF_DISTRIBUTION_ID,
    SITE_URL: process.env.SITE_URL,
    VITE_SITE_URL: process.env.SITE_URL,
    CACHE_TTL: process.env.CACHE_TTL,
    CACHE_URL: process.env.CACHE_URL,
    BETTER_AUTH_SECRET: process.env.BETTER_AUTH_SECRET,
    BETTER_AUTH_URL: process.env.BETTER_AUTH_URL,
    GITHUB_CLIENT_ID: process.env.GITHUB_CLIENT_ID,
    GITHUB_CLIENT_SECRET: process.env.GITHUB_CLIENT_SECRET,
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    CACHE_TTL_SECONDS: process.env.CACHE_TTL_SECONDS,
    VITE_CACHE_TTL_SECONDS: process.env.CACHE_TTL_SECONDS,
    BUNDLED_DATA_STALE_TIME_SECONDS: process.env.BUNDLED_DATA_STALE_TIME_SECONDS,
    MAX_STALE_WHILE_REVALIDATE_SECONDS: process.env.MAX_STALE_WHILE_REVALIDATE_SECONDS,
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
    SENTRY_DSN: process.env.SENTRY_DSN,
    VITE_SENTRY_DSN: process.env.SENTRY_DSN,
  },
});

export type Env = typeof env;
